import { API } from '@/modules/api'
import type { TActivityCartLine } from '@/types/cart'
import { defineStore } from 'pinia'
import type { ActivityFee } from '@/types/activity'
import { useCartStore } from '@/stores/cart'
import { OrderItemType, OrderItemTypeCode } from '@/types/orderItemType'
import { useStore } from '@/stores'

export const createUniqueId = (feeId: number) =>
  feeId + '::' + OrderItemTypeCode.AffinetyActivityFee

export const useAffinetyActivityStore = defineStore('affinetyActivity', {
  state: () => ({
    isLoaded: {} as Record<TStudentAccountId, boolean>,
    activities: {} as Record<TStudentAccountId, ActivityFee[]>
  }),

  getters: {
    isLoading(state) {
      return (studentAccountId: TStudentAccountId) => !(state.isLoaded[studentAccountId] ?? false)
    },

    // COMMENT HERE FOR EXPLANATION
    getSingleActivityRestrictionEnabled(state) {
      return (family: TFamily) => {
        let restrictionEnabled = false
        const store = useStore()
        const students = store.getStudentsInFamily(family.familyAccountId)
        students.forEach(student => {
          if (
            this.getActivities(student.studentAccountId).filter(activity => {
              return activity.hasFamilyMax || activity.hasParticipationFee
            }).length > 0
          ) {
            restrictionEnabled = true
          }
        })
        return restrictionEnabled
      }
    },

    getActivities(state) {
      return (studentAccountId: TStudentAccountId) => state.activities[studentAccountId] ?? []
    }
  },

  actions: {
    async fetch(_districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      try {
        this.isLoaded[studentAccountId] = false
        this.activities[studentAccountId] = await API.Get.affinetyActivities(studentAccountId)
        this.autoAdd(_districtId, studentAccountId)
        this.validateCart(_districtId, studentAccountId)
        this.isLoaded[studentAccountId] = true
      } catch (e) {
        this.isLoaded[studentAccountId] = true
        throw e
      }
    },

    autoAdd(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      this.activities[studentAccountId].forEach(feeData => {
        if (!feeData.hasFamilyMax) {
          const cartData: Omit<TActivityCartLine, 'orderItemType' | 'uniqueId'> = {
            studentAccountId: studentAccountId,
            feeId: feeData.feeId,
            amount: feeData.amountDue,
            description: feeData.description
          }

          this.addToCart(districtId, cartData)
        }
      })
    },

    singleActivityCheckout(studentAccountId: TStudentAccountId) {
      return (
        this.getActivities(studentAccountId).filter(activity => {
          return activity.hasFamilyMax || activity.hasParticipationFee
        }).length > 0
      )
    },

    // This function is used to validate the cart lines for the affinety activities store
    // It checks if the cart lines are valid compared to the data from the API, and if not, it removes them from the cart.
    // This prevents the cart from attempting to get order line estimates or checking out with invalid data.
    validateCart(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      const cartStore = useCartStore()

      for (const line of cartStore.getCartLines(districtId) as TActivityCartLine[]) {
        if (
          line.orderItemType === OrderItemType.Activity &&
          line.studentAccountId === studentAccountId
        ) {
          const activity = this.activities[studentAccountId].find(
            activity => activity.feeId === line.feeId
          )
          if (!activity) {
            console.log('activity not found, removing from cart', line.feeId)
            this.removeFromCart(districtId, line.feeId)
          }
          // ensure fee amount is less than cart amountn meaning cart amount is too high
          if (activity && activity.amountDue < line.amount) {
            console.log('activity amount is less than cart amount, adjusting cart', line.feeId)
            cartStore.updateCartLine(districtId, line.uniqueId, {
              ...line,
              amount: activity.amountDue
            })
          }
        }
      }
    },

    async removeActivityFee(
      districtId: TDistrictId,
      activityFeeId: number,
      studentAccountId: TStudentAccountId
    ) {
      API.Post.removeActivityFee(districtId, activityFeeId)
        .then(() => {
          const activityIndex = this.activities[studentAccountId].findIndex(
            activity => activity.feeId === activityFeeId
          )
          this.activities[studentAccountId].splice(activityIndex, 1)
          this.removeFromCart(districtId, activityFeeId)
        })
        .catch(error => {
          console.error('Failed to remove fee:', error)
        })
    },

    addToCart(
      districtId: TDistrictId,
      activityData: Omit<TActivityCartLine, 'uniqueId' | 'orderItemType'>
    ) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(activityData.feeId)
      const existingLine = cartStore.getCartLine(districtId, uniqueId)

      if (existingLine) {
        cartStore.updateCartLine(districtId, uniqueId, activityData)
      } else {
        cartStore.addCartLine(districtId, uniqueId, {
          ...activityData,
          uniqueId,
          orderItemType: OrderItemType.Activity
        })
      }
    },

    removeFromCart(districtId: TDistrictId, feeId: number) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeId)
      cartStore.removeCartLine(districtId, uniqueId)
    }
  }
})
